import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactNode, useCallback, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';

import { styles } from '../../styles';
import { BaseSelect, BaseSelectProps } from '../baseSelect';
import {
  defaultRenderOption as baseRenderOption,
  defaultExtractKey,
} from '../baseSelect/default';

const ValuesContainer = styled.div`
  display: flex;
  overflow: auto;
  ${styles.mr.sm}
`;

const itemSizes = {
  sm: css`
    ${styles.padding.sm};
    padding: ${(p) => p.theme.spaces.sm} ${(p) => p.theme.spaces.sm};
  `,
  md: css`
    ${styles.py.sm};
    ${styles.px.md};
  `,
};

const ValueItem = styled.div<{ size?: 'sm' | 'md' }>`
  ${styles.bg.lightBg};
  ${styles.mr.md};
  display: flex;
  align-items: center;
  ${(p) => itemSizes[p.size || 'md']}
`;

const CloseItem = styled.div`
  ${styles.ml.md}
  ${styles.transition.default}
  cursor: pointer;
  :hover {
    ${styles.color.primary}
  }
`;

const defaultRenderOption = (
  option: any,
  o: { active?: boolean; placeholder?: boolean; inline?: boolean }
) => {
  if (o.inline) {
    return <span>{option.text || option.label || option.value}</span>;
  }
  return baseRenderOption(option, o);
};

export function SelectMulti<Option>({
  value,
  extractKey = defaultExtractKey,
  renderOption = defaultRenderOption,
  onSelect,
  size,
  ...props
}: {
  value?: any[];
  onSelect?: (opt: Option[]) => void;
  renderOption?: (
    opt: Option,
    o: { active?: boolean; placeholder?: boolean; inline?: boolean }
  ) => ReactNode;
} & Omit<
  BaseSelectProps<Option>,
  'renderContent' | 'isOptionSelected' | 'onSelect' | 'renderOption'
>) {
  const optByKey = useMemo(() => {
    const res: Record<string, Option> = {};
    if (Array.isArray(props.options)) {
      props.options.forEach((opt, idx) => {
        res[extractKey(opt, idx)] = opt;
      });
    }
    return res;
  }, [extractKey, props.options]);

  const selected = useMemo(() => {
    const selectedSet = new Set<Option>();
    const arr: Option[] = [];
    if (Array.isArray(value)) {
      value.forEach((v) => {
        const opt = optByKey[v];
        if (opt) {
          selectedSet.add(opt);
          arr.push(opt);
        }
      });
    }

    return {
      arr,
      set: selectedSet,
    };
  }, [optByKey, value]);

  const isOptionSelected = useMemo(() => {
    return (opt: Option) => {
      return selected.set.has(opt);
    };
  }, [selected.set]);

  const cb = { onSelect };
  const cbRef = useRef<typeof cb>(null);
  cbRef.current = cb;

  const renderContent = useCallback(() => {
    return (
      <ValuesContainer>
        {selected.arr.map((v, idx) => (
          <ValueItem size={size} key={extractKey(v, idx)}>
            {renderOption(v, {
              placeholder: false,
              active: false,
              inline: true,
            })}
            <CloseItem
              onClick={() => {
                if (typeof cbRef.current.onSelect === 'function') {
                  cbRef.current.onSelect(selected.arr.filter((o) => o !== v));
                }
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </CloseItem>
          </ValueItem>
        ))}
      </ValuesContainer>
    );
  }, [extractKey, renderOption, selected.arr]);

  return (
    <BaseSelect
      renderContent={renderContent}
      isOptionSelected={isOptionSelected}
      extractKey={extractKey}
      renderOption={renderOption}
      size={size}
      onSelect={(opt) => {
        if (typeof onSelect === 'function') {
          if (selected.set.has(opt)) {
            onSelect(selected.arr.filter((o) => o !== opt));
          } else {
            onSelect([...selected.arr, opt]);
          }
        }
      }}
      {...props}
    />
  );
}
